import React from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./components/LanguageSwitcher";

const StrategicPartners = () => {
  return (
    <section id="socios" className="py-16 bg-gray-950 text-white text-center">
      <h2 className="text-3xl md:text-4xl font-bold mb-4">
        Aliados que Impulsan Nuestro Juego
      </h2>
      <p className="text-lg max-w-3xl mx-auto mb-10">
        Desde tecnología de punta hasta visión compartida, nuestros socios estratégicos
        son parte esencial del crecimiento de la Puerto Rico Esports League.
        Cada alianza representa una pieza clave en el ecosistema que estamos construyendo.
      </p>
      <div className="flex justify-center items-center gap-10 flex-wrap">
        <a href="https://anttera.com" target="_blank" rel="noopener noreferrer">
          <img src="ant.png" alt="AntTera IT Services" className="h-24 hover:scale-105 transition-transform duration-300" />
        </a>
      </div>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-4 max-w-6xl mx-auto">
        <img src="/distrito1.jpg" alt="Evento Distrito 1" className="rounded-xl shadow-md object-cover w-full h-64" />
        <img src="/distrito2.jpg" alt="Evento Distrito 2" className="rounded-xl shadow-md object-cover w-full h-64" />
        <img src="/distrito4.jpg" alt="Evento Distrito 4" className="rounded-xl shadow-md object-cover w-full h-64" />
      </div>
    </section>
  );
};

export default function LandingPage() {
  const { t } = useTranslation();

  const pastEvents = [
    { title: "Just Dance", date: "Viernes 18 de abril", img: "/just-dance.png", desc: "Arrancamos el fin de semana con ritmo." },
    { title: "Marvel Rivals", date: "Sábado 19 de abril", img: "/marvel-rivals.jpg", desc: "Enfrentamiento heroico con 4 equipos." },
    { title: "Fortnite", date: "Domingo 20 de abril", img: "/fortnite.jpg", desc: "Kill Race 'First to 3 Top Spot' con 10 jugadores." },
    { title: "Friday Night Esports", date: "8 de noviembre", img: "/friday.png", desc: "Torneos de Rocket League, NBA 2K25 y Mortal Kombat 1 en Casino del Mar." },
    { title: "Comic Blast - Fortnite", date: "7 de septiembre", img: "/cb24.png", desc: "Torneo Kill Race en Plaza Carolina, powered by AntTera." },
    { title: "US Army Battleground", date: "2 de junio, 2024", img: "/usbat.png", desc: "Call of Duty Modern Warfare III en Distrito T-Mobile." },
    { title: "Mortal Kombat Torneo", date: "Oct 14, Nov 4, Nov 11", img: "/mortalk.png", desc: "Torneo de Mortal Kombat presentado con AntTera." },
    { title: "NBA 2K23 Blast", date: "9 de septiembre", img: "/Comicblastpromo.png", desc: "Torneo 1v1 en Comic Blast con transmisión en vivo." },
    { title: "Be A Legend Round 2", date: "27 de agosto", img: "/balr2.png", desc: "Evento especial 'Be A Legend' transmitido en vivo." },
  ];

  const youtubeClips = ["BShARexlrZ4", "xgPDXBgdEws", "4M1aVF42Gec", "IynmdYtjej0", "T4yjb2MLdqE", "42u_fgtZWzE"];

  const brands = [
    "wipr.png", "PRTV.png", "wwe.png", "mtv.png", "VH1_logonew.svg.png",
    "Dominos_pizza_logo.svg.png", "Distrito_T-Mobile_Logo.png",
    "army.png", "BetMGM_CDM.png", "TIIKE_LOGO.png", "nba.png", "prcc.png"
  ];

  const socialMedia = [
    { name: "Facebook", icon: "Facebooklogo.png", url: "https://www.facebook.com/presportsleague" },
    { name: "Instagram", icon: "instagramlogo.png", url: "https://www.instagram.com/presportsleague" },
    { name: "Twitter", icon: "xlogo.png", url: "https://twitter.com/presportsleague" }
  ];  

  const handleImageError = (e, filename) => {
    console.error(`Error al cargar: ${filename}`);
    e.target.style.display = 'none';
  };

  return (
    <>
      <Helmet>
        <title>Puerto Rico Esports League - Comunidad y Competencia</title>
        <meta name="description" content="Explora Puerto Rico Esports League: la primera liga profesional de videojuegos en la isla con eventos, TV, comunidad y más." />
        <meta property="og:image" content="/logo.png" />
      </Helmet>

      <div className="bg-black text-white font-sans">
        <section className="relative h-[60vh] w-full overflow-hidden">
          <video className="absolute top-0 left-0 w-full h-full object-cover brightness-75" autoPlay loop muted playsInline onError={(e) => { e.target.style.display = 'none'; document.getElementById('hero-fallback').style.display = 'block'; }}>
            <source src="/hero-video-optimized.mp4" type="video/mp4" />
          </video>
          <img id="hero-fallback" src="/logo.png" alt="Fallback" className="hidden w-full h-full object-cover" />
          <img src="/logo.png" alt="Logo PR Esports League" className="absolute top-6 left-6 h-14 opacity-60" />
          <LanguageSwitcher />
        </section>

        <div className="bg-[#0d0d0d] pt-20 px-6 text-center">
          <div className="container mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">PUERTO RICO ESPORTS LEAGUE</h1>
            <p className="text-xl sm:text-2xl text-[#00f0ff] font-bold mb-16 animate-pulse">Dominamos el escenario digital de Puerto Rico</p>
          </div>
        </div>

        <section className="bg-[#0d0d0d] py-20 px-6">
          <div className="container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div className="text-right lg:pr-10">
              <h2 className="text-3xl font-bold mb-8">¿Quiénes Somos?</h2>
              <p className="text-lg text-gray-300 mb-6">
                <strong>Puerto Rico Esports League</strong> no es una liga en el sentido tradicional. Es un ecosistema único de entretenimiento competitivo que combina esports, producción audiovisual, eventos en vivo y formación educativa. Fundada en 2019, somos la primera y única organización de su tipo en Puerto Rico con presencia constante en televisión nacional y plataformas digitales, desarrollando una programación original centrada en la cultura del gaming.

Nos hemos consolidado como una plataforma de exposición de talentos y un motor de mercadeo para la industria de los esports, catapultando jugadores, creadores de contenido, artistas y marcas a nuevas audiencias. Nuestra misión es posicionar a Puerto Rico como un hub estratégico del gaming competitivo en las Américas, conectando comunidades, sectores privados y entidades gubernamentales bajo una visión en la que el gaming se convierte en herramienta de innovación, desarrollo económico y evolución cultural.

                <img src="WEBLONG3_optimized.jpg" alt="Quiénes Somos Gráfica" className="w-full max-w-4xl mx-auto mb-10 rounded-xl" />
              </p>
              <h3 className="text-2xl font-bold text-[#00f0ff] mb-4">Nuestra Visión</h3>
              <p className="text-lg text-gray-300 mb-6">Convertir a Puerto Rico en el epicentro del ecosistema gamer del Caribe y América Latina, desarrollando una infraestructura que conecte jugadores, instituciones educativas, empresas privadas y organismos públicos. Aspiramos a construir una liga de clase mundial con proyección global que sirva de modelo para el crecimiento económico, tecnológico y creativo en la región.</p>
              <img src="WEBLONG1_optimized.jpg" alt="Nuestra Visión Gráfica" className="w-full max-w-4xl mx-auto mb-10 rounded-xl" />
 <h3 className="text-2xl font-bold text-[#00f0ff] mb-4">Nuestra Trayectoria</h3>
<ul className="text-lg text-gray-300 space-y-3 mb-6 pl-0 list-none">
  <li>+48 episodios transmitidos en televisión nacional a través de WIPR Canal 6</li>
  <li>Eventos multitudinarios en lugares icónicos alcanzando más de 6,000 asistentes</li>
  <li>Colaboraciones con marcas como Monster Energy, US Army, MTV, VH1 y WWE</li>
  <li>Producción de torneos para Fortnite, Rocket League, NBA 2K y Call of Duty y más</li>
  <li>Iniciativas educativas en escuelas y residenciales públicos</li>
  <li>Proyectos educativos en escuelas y comunidades de residenciales públicos con enfoque en inclusión, alfabetización digital y carreras en videojuegos.</li>
  <img src="WEBLONG2_optimized.jpg" alt="Nuestra Trayectoria Gráfica" className="w-full max-w-4xl mx-auto mb-10 rounded-xl" />
</ul>
              <h3 className="text-2xl font-bold text-[#00f0ff] mb-4">¿Qué nos hace únicos?</h3>
              <ul className="text-lg text-gray-300 space-y-3 pl-0 list-none">
                <li>Somos productores de contenido original multiplataforma, integrando TV, digital y eventos.</li>
                <li>Contamos con un equipo multidisciplinario especializado en producción, esports, diseño de videojuegos, marketing, casting y animación.</li>
                <li>Ofrecemos una estructura propia y sostenible de torneos, talleres y media que sirve de puente hacia el mercado internacional.</li>
                <li>Tenemos un historial probado de colaboraciones con gobierno y sector privado, creando un ecosistema innovador y de impacto social.</li>
                <li>Pioneros en llevar los esports a contextos no tradicionales en Puerto Rico, como residenciales públicos y espacios educativos.</li>
              </ul>
            </div>
            <div className="text-left lg:pl-10">
              <h2 className="text-3xl font-bold mb-8">Nuestro Alcance</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
                <div className="bg-[#1f1f1f] p-6 rounded-xl shadow-lg"><h3 className="text-3xl font-bold text-pink-500 mb-2">+25.1</h3><p className="text-gray-300">millones de impresiones digitales anuales</p></div>
                <div className="bg-[#1f1f1f] p-6 rounded-xl shadow-lg"><h3 className="text-3xl font-bold text-pink-500 mb-2">6K+</h3><p className="text-gray-300">asistentes en eventos presenciales en 2024-2025</p></div>
                <div className="bg-[#1f1f1f] p-6 rounded-xl shadow-lg"><h3 className="text-3xl font-bold text-pink-500 mb-2">30+</h3><p className="text-gray-300">Marcas colaboradoras</p></div>
                <div className="bg-[#1f1f1f] p-6 rounded-xl shadow-lg"><h3 className="text-3xl font-bold text-pink-500 mb-2">30%</h3><p className="text-gray-300">Alcance internacional de nuestra audiencia en LATAM y Estados Unidos</p></div>
              </div>
              <h3 className="text-2xl font-bold text-[#00f0ff] mb-4">Conectamos a:</h3>
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-[#1a1a1a] p-4 rounded-lg"><p className="font-semibold">Jugadores profesionales</p></div>
                <div className="bg-[#1a1a1a] p-4 rounded-lg"><p className="font-semibold">Instituciones educativas</p></div>
                <div className="bg-[#1a1a1a] p-4 rounded-lg"><p className="font-semibold">Gobiernos</p></div>
                <div className="bg-[#1a1a1a] p-4 rounded-lg"><p className="font-semibold">Marcas globales</p></div>
              </div>
              <StrategicPartners />
            </div>
          </div>
        </section>
        <section id="eventos-recientes" className="py-16 px-8 bg-black text-white">
  <div className="max-w-7xl mx-auto text-center">
    <h2 className="text-4xl font-bold mb-10">Eventos Recientes</h2>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      
      <div className="flex flex-col items-center">
        <div className="aspect-w-16 aspect-h-9 w-full">
          <iframe
            className="w-full h-full rounded-lg"
            src="https://www.youtube.com/embed/SfsotLUQde0"
            title="US Army Battleground Recap"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
        <h3 className="text-xl font-semibold mt-4">US Army Battleground</h3>
        <p className="text-sm mt-2 px-4">Revive los mejores momentos del evento que revolucionó el gaming en Puerto Rico.</p>
      </div>

      <div className="flex flex-col items-center">
        <div className="aspect-w-16 aspect-h-9 w-full">
          <iframe
            className="w-full h-full rounded-lg"
            src="https://www.youtube.com/embed/GCcQH0qP0SQ"
            title="Metro City Kumite Recap"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
        <h3 className="text-xl font-semibold mt-4">Metro City Kumite</h3>
        <p className="text-sm mt-2 px-4">Competencia épica de Mortal Kombat 1 donde los mejores peleadores de la isla mostraron su talento.</p>
      </div>

      <div className="flex flex-col items-center">
        <div className="aspect-w-16 aspect-h-9 w-full">
          <iframe
            className="w-full h-full rounded-lg"
            src="https://www.youtube.com/embed/JO5b4eYqhEs"
            title="School Wars Recap"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
        <h3 className="text-xl font-semibold mt-4">School Wars</h3>
        <p className="text-sm mt-2 px-4">
          Mira cómo los mejores talentos estudiantiles se enfrentaron en nuestro torneo escolar de esports.
        </p>
      </div>
      </div>
      </div>
      </section>
    <section id="clips-tv" className="py-16 px-8 bg-black text-white">
  <div className="max-w-7xl mx-auto text-center">
    <h2 className="text-4xl font-bold mb-10">Puerto Rico Esports League: The Show - Clips de TV</h2>

    {/* Bloque de acceso a PRTV+ */}
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', justifyContent: 'center', marginBottom: '2rem' }}>
      <p style={{ fontSize: '1.2rem', margin: 0 }}>
        Accede a PRTV+ y disfruta de todos los episodios
      </p>
      <a 
        href="https://apps.apple.com/us/app/prtv/id6446822315" 
        target="_blank" 
        rel="noopener noreferrer"
      >
        <img 
          src="PRTV.png" 
          alt="Descargar PRTV+" 
          style={{ maxWidth: '100px', height: 'auto' }}
        />
      </a>
    </div>

    {/* Clips de TV organizados */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {[
        { id: "BShARexlrZ4", title: "El Nuevo Nivel de Combate", desc: "En este emocionante segmento, nos sumergimos en el emocionante mundo de Street Fighter 6," },
        { id: "xgPDXBgdEws", title: "Inspiración y superación con Winter Samurai", desc: "Winter Samurai nos revelará sus estrategias y técnicas únicas" },
        { id: "4M1aVF42Gec", title: "Encuentro con Master Chief en Halo Infinite!", desc: "nos sumergimos en una partida de Halo Infinite repleta de acción" },
        { id: "IynmdYtjej0", title: "¡Apex Legends en llamas!", desc: "jugadores de Apex Legends se enfrentan" },
        { id: "T4yjb2MLdqE", title: "Guilty Gear! Diegubis vs. Neovermillion", desc: "Dos maestros de la lucha se verán las caras mientras el destino del universo pende de un hilo" },
        { id: "42u_fgtZWzE", title: "Dominik Mysterio y WWE2K", desc: "Dominik Mysterio! Prepárate para sumergirte en una experiencia llena de acción" }
      ].map((clip, idx) => (
        <div key={idx} className="flex flex-col items-center">
          <div className="aspect-w-16 aspect-h-9 w-full">
            <iframe
              className="w-full h-full rounded-lg"
              src={`https://www.youtube.com/embed/${clip.id}`}
              title={clip.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
          <h3 className="text-xl font-semibold mt-4">{clip.title}</h3>
          <p className="text-sm mt-2 px-4">{clip.desc}</p>
        </div>
      ))}
    </div>

  </div>
</section>

<section className="bg-[#0d0d0d] py-20 px-6 text-center">
  <h2 className="text-3xl font-bold mb-8">Próximos Eventos</h2>
  <div className="max-w-2xl mx-auto">
    <p className="text-xl text-gray-300 mb-6">¡Stay tuned para más información!</p>
    <div className="bg-[#1c1c1c] rounded-lg p-8">
      <p className="text-lg text-pink-400 font-semibold">Próximamente anunciaremos nuevas fechas</p>
    </div>
  </div>
</section>

        <section className="bg-[#0d0d0d] py-20 px-6 text-center">
          <h2 className="text-3xl font-bold mb-8">Eventos Pasados</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pastEvents.map((event, idx) => (
              <div key={idx} className="bg-[#1c1c1c] rounded-lg p-4 shadow-md">
                <img src={event.img} alt={event.title} className="w-full h-48 object-cover rounded mb-3" loading="lazy" onError={(e) => handleImageError(e, event.img)} />
                <h3 className="text-xl font-bold">{event.title}</h3>
                <p className="text-pink-400 font-semibold">{event.date}</p>
                <p className="text-sm text-gray-300">{event.desc}</p>
              </div>
            ))}
          </div>
        </section>

        

        <section className="bg-[#121212] py-20 px-6 text-center">
          <h2 className="text-3xl font-bold mb-8">Marcas que han confiado en nosotros</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {brands.map((logo, idx) => (
              <img key={idx} src={`/${logo}`} alt={logo.replace(/\.[^/.]+$/, "")} className="h-16 mx-auto object-contain" loading="lazy" onError={(e) => handleImageError(e, logo)} />
            ))}
          </div>
        </section>

        <section id="contacto" className="bg-[#0d0d0d] py-20 px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">Contáctanos</h2>
          <form action="https://formspree.io/f/xdkebvoz" method="POST" className="max-w-xl mx-auto space-y-4">
            <input type="text" name="nombre" placeholder="Tu nombre" className="w-full p-3 rounded bg-[#1c1c1c] border border-[#333]" required />
            <input type="email" name="email" placeholder="Tu correo" className="w-full p-3 rounded bg-[#1c1c1c] border border-[#333]" required />
            <textarea name="mensaje" rows="4" placeholder="Tu mensaje" className="w-full p-3 rounded bg-[#1c1c1c] border border-[#333]" required></textarea>
            <button type="submit" className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-500 font-semibold rounded-lg hover:scale-105 transition">Enviar Mensaje</button>
          </form>
        </section>

        <footer className="bg-black py-10 text-center text-gray-500 text-sm">
          <div className="flex justify-center space-x-6 mb-4">
            {socialMedia.map((social, idx) => (
              <a key={idx} href="#" target="_blank" rel="noopener noreferrer">
                <img src={`/${social.icon}`} alt={social.name} className="h-6" onError={(e) => handleImageError(e, social.icon)} />
              </a>
            ))}
          </div>
          <p>© {new Date().getFullYear()} Puerto Rico Esports League. Todos los derechos reservados.</p>
        </footer>
      </div>
    </>
  );
}
